import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { api } from '../api';
import { useAuth } from '../App';
import DenialModal from '../components/DenialModal';
import { formatBytes } from '../utils/uploadFiles';

const ADMIN_COLUMNS = [
  { key: 'submitted', label: 'Submitted' },
  { key: 'reviewed', label: 'Reviewed' },
  { key: 'approved', label: 'Approved' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'denied', label: 'Denied' },
];

const SPAM_GROUP = { key: 'spam', label: 'Spam', match: ['spam'] };

const PUBLIC_GROUPS = [
  { key: 'received', label: 'Received', match: ['submitted', 'reviewed'] },
  { key: 'in_progress', label: 'In Progress', match: ['approved', 'in_progress'] },
  { key: 'completed', label: 'Completed', match: ['completed'] },
  { key: 'declined', label: 'Declined', match: ['denied'] },
];

const ADMIN_GROUPS = [
  { key: 'submitted', label: 'Submitted', match: ['submitted'] },
  { key: 'reviewed', label: 'Reviewed', match: ['reviewed'] },
  { key: 'approved', label: 'Approved', match: ['approved'] },
  { key: 'in_progress', label: 'In Progress', match: ['in_progress'] },
  { key: 'completed', label: 'Completed', match: ['completed'] },
  { key: 'denied', label: 'Denied', match: ['denied'] },
  { key: 'spam', label: 'Spam', match: ['spam'] },
];

const LOCATIONS = [
  { key: 'all', label: 'All Locations' },
  { key: 'church', label: 'Church' },
  { key: 'pastors_house', label: "Pastor's House" },
];

function timeAgo(dateStr) {
  const now = new Date();
  const then = new Date(dateStr);
  const days = Math.floor((now - then) / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function locationLabel(loc) {
  return loc === 'church' ? 'Church' : "Pastor's House";
}

function priorityOf(ticket) {
  return ticket.trustee_priority || ticket.submitter_priority;
}

const STATUS_COLORS = {
  submitted: 'var(--blue)', reviewed: 'var(--purple)', approved: 'var(--gold)',
  in_progress: 'var(--orange)', completed: 'var(--green)', denied: 'var(--red)',
  spam: 'var(--text-muted)', received: 'var(--blue)', declined: 'var(--red)',
};

const SPAM_CLEANUP_VISIBLE_BYTES = 5 * 1024 * 1024;

export default function Board() {
  const [tickets, setTickets] = useState([]);
  const [trustees, setTrustees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem('boardView') || 'list');
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState(new Set());
  const [locationFilter, setLocationFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [showSpam, setShowSpam] = useState(false);
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('boardSort') || 'newest');
  const [collapsed, setCollapsed] = useState({});
  const [boardCollapsed, setBoardCollapsed] = useState({});
  const [visibleCounts, setVisibleCounts] = useState({});
  const [boardVisibleCounts, setBoardVisibleCounts] = useState({});
  const [denialTarget, setDenialTarget] = useState(null);
  const [purgingSpamAttachments, setPurgingSpamAttachments] = useState(false);
  const [spamAttachmentSummary, setSpamAttachmentSummary] = useState(null);
  const [spamAttachmentSummaryError, setSpamAttachmentSummaryError] = useState(false);
  const [confirmingSpamCleanup, setConfirmingSpamCleanup] = useState(false);
  const [toast, setToast] = useState(null);
  const searchRef = useRef();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user;
  const canCleanSpam = !!user?.isAdmin;
  const effectiveView = isAdmin ? view : 'list';

  const groups = isAdmin ? (showSpam ? [SPAM_GROUP] : ADMIN_GROUPS) : PUBLIC_GROUPS;
  const columns = showSpam ? [SPAM_GROUP] : ADMIN_COLUMNS;

  const loadTickets = useCallback(async () => {
    try {
      const data = await api.getTickets();
      setTickets(data);
    } catch (err) {
      console.error('Failed to load tickets:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
    if (isAdmin) { api.getTrustees().then(setTrustees).catch(() => {}); }
  }, [loadTickets, isAdmin]);

  useEffect(() => {
    if (!showSpam || !canCleanSpam) {
      setSpamAttachmentSummary(null);
      setSpamAttachmentSummaryError(false);
      setConfirmingSpamCleanup(false);
      return;
    }

    setSpamAttachmentSummaryError(false);
    api.getSpamAttachmentSummary()
      .then(setSpamAttachmentSummary)
      .catch(() => {
        setSpamAttachmentSummary(null);
        setSpamAttachmentSummaryError(true);
      });
  }, [showSpam, canCleanSpam]);

  // Focus search when filter bar opens
  useEffect(() => {
    if (showFilters && searchRef.current) searchRef.current.focus();
  }, [showFilters]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 5000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const hasActiveFilters = search || selectedStatuses.size > 0 || locationFilter !== 'all' || assigneeFilter !== 'all' || showSpam;

  const toggleStatus = (key) => {
    setSelectedStatuses(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedStatuses(new Set());
    setLocationFilter('all');
    setAssigneeFilter('all');
    setShowSpam(false);
  };

  const toggleSpamView = () => {
    setShowSpam(prev => !prev);
    setSelectedStatuses(new Set());
  };

  const handlePurgeSpamAttachments = async () => {
    setPurgingSpamAttachments(true);
    try {
      const result = await api.purgeSpamAttachments();
      await loadTickets();
      setSpamAttachmentSummary({ fileCount: 0, totalBytes: 0 });
      setSpamAttachmentSummaryError(false);
      setConfirmingSpamCleanup(false);
      setToast({
        type: 'success',
        message: result.deletedCount > 0
          ? `Cleared ${result.deletedCount} uploaded file${result.deletedCount === 1 ? '' : 's'} from spam requests.`
          : 'There were no spam files to clear.',
      });
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setPurgingSpamAttachments(false);
    }
  };

  const changeSort = (val) => {
    setSortBy(val);
    localStorage.setItem('boardSort', val);
  };

  const handleToggleStar = async (e, ticketId) => {
    e.stopPropagation();
    try {
      const { starred } = await api.toggleStar(ticketId);
      setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, starred } : t));
    } catch (err) { console.error('Star toggle failed:', err); }
  };

  const DEFAULT_VISIBLE = 5;
  const LOAD_MORE = 10;

  const getVisibleCount = (key) => visibleCounts[key] || DEFAULT_VISIBLE;
  const showMoreList = (key) => setVisibleCounts(prev => ({ ...prev, [key]: (prev[key] || DEFAULT_VISIBLE) + LOAD_MORE }));
  const showLessList = (key) => setVisibleCounts(prev => ({ ...prev, [key]: DEFAULT_VISIBLE }));

  const getBoardVisibleCount = (key) => boardVisibleCounts[key] || DEFAULT_VISIBLE;
  const showMoreBoard = (key) => setBoardVisibleCounts(prev => ({ ...prev, [key]: (prev[key] || DEFAULT_VISIBLE) + LOAD_MORE }));
  const showLessBoard = (key) => setBoardVisibleCounts(prev => ({ ...prev, [key]: DEFAULT_VISIBLE }));

  const toggleCollapse = (key) => {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Apply all filters
  const filtered = tickets.filter(t => {
    // Spam handling
    if (!isAdmin && t.status === 'spam') return false;
    if (isAdmin && showSpam) return t.status === 'spam'; // show ONLY spam
    if (isAdmin && !showSpam && t.status === 'spam') return false;

    // Assignee
    if (assigneeFilter !== 'all') {
      if (assigneeFilter === 'me') {
        if (t.assigned_trustee_id !== user?.id) return false;
      } else if (assigneeFilter === 'unassigned') {
        if (t.assigned_trustee_id) return false;
      } else {
        if (t.assigned_trustee_id !== assigneeFilter) return false;
      }
    }

    // Location
    if (locationFilter !== 'all' && t.location !== locationFilter) return false;

    // Status — multi-select
    if (selectedStatuses.size > 0) {
      if (isAdmin) {
        if (!selectedStatuses.has(t.status)) return false;
      } else {
        // Map selected public keys to internal statuses
        const allowedInternal = new Set();
        for (const key of selectedStatuses) {
          const group = PUBLIC_GROUPS.find(g => g.key === key);
          if (group) group.match.forEach(s => allowedInternal.add(s));
        }
        if (!allowedInternal.has(t.status)) return false;
      }
    }

    // Search
    if (search) {
      const q = search.toLowerCase();
      const haystack = [
        t.title, t.description, t.specific_location,
        locationLabel(t.location), `#${t.ticket_number}`,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(q)) return false;
    }

    return true;
  });

  // Sort function — starred first, then by selected sort
  const PRIORITY_ORDER = { urgent: 0, high: 1, medium: 2, low: 3 };
  const sortTickets = (list) => {
    return [...list].sort((a, b) => {
      // Starred always first
      if (a.starred && !b.starred) return -1;
      if (!a.starred && b.starred) return 1;

      switch (sortBy) {
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'priority':
          const pa = PRIORITY_ORDER[priorityOf(a)] ?? 2;
          const pb = PRIORITY_ORDER[priorityOf(b)] ?? 2;
          if (pa !== pb) return pa - pb;
          return new Date(b.created_at) - new Date(a.created_at);
        case 'updated':
          return new Date(b.updated_at) - new Date(a.updated_at);
        case 'newest':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });
  };

  // Group filtered tickets
  const groupedList = groups.map(group => ({
    ...group,
    tickets: sortTickets(filtered.filter(t => group.match.includes(t.status))),
  })).filter(g => g.tickets.length > 0);

  const totalCount = isAdmin
    ? tickets.length
    : tickets.filter(t => t.status !== 'spam').length;

  const toggleBoardCollapse = (key) => {
    setBoardCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Board drag — preserves drop position
  const handleDragEnd = async (result) => {
    if (!isAdmin) return;
    const { draggableId, source, destination } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const newStatus = destination.droppableId;
    const ticket = tickets.find(t => t.id === draggableId);
    if (!ticket) return;

    if (newStatus === 'denied' && ticket.status !== 'denied') {
      setDenialTarget({ ticketId: draggableId });
      return;
    }

    // Auto-expand collapsed column on drop
    if (boardCollapsed[newStatus]) {
      setBoardCollapsed(prev => ({ ...prev, [newStatus]: false }));
    }

    // Optimistic update
    setTickets(prev => prev.map(t =>
      t.id === draggableId ? { ...t, status: newStatus } : t
    ));

    if (ticket.status !== newStatus) {
      try { await api.updateTicket(draggableId, { status: newStatus }); } catch { loadTickets(); }
    }
  };

  const handleDeny = async (reason) => {
    if (!denialTarget) return;
    try {
      await api.updateTicket(denialTarget.ticketId, { status: 'denied', denial_reason: reason });
      setTickets(prev => prev.map(t => t.id === denialTarget.ticketId ? { ...t, status: 'denied', denial_reason: reason } : t));
    } catch { loadTickets(); }
    finally { setDenialTarget(null); }
  };

  if (loading) return <div className="spinner" />;

  const boardGrouped = {};
  columns.forEach(col => { boardGrouped[col.key] = []; });
  filtered.forEach(t => {
    if (boardGrouped[t.status]) boardGrouped[t.status].push(t);
  });
  // Sort within each board column
  columns.forEach(col => { boardGrouped[col.key] = sortTickets(boardGrouped[col.key]); });

  // When status filters are active, show only the selected status columns
  const visibleColumns = selectedStatuses.size > 0
    ? columns.filter(col => selectedStatuses.has(col.key))
    : columns;
  const showSpamCleanup = showSpam && canCleanSpam && (
    spamAttachmentSummaryError ||
    (spamAttachmentSummary && spamAttachmentSummary.totalBytes >= SPAM_CLEANUP_VISIBLE_BYTES)
  );

  const spamCleanupToolbar = showSpamCleanup && (
    <div className="spam-section-toolbar">
      <div>
        <span>Spam cleanup</span>
        <p>
          {spamAttachmentSummaryError
            ? 'Could not estimate storage. Cleanup can still run.'
            : spamAttachmentSummary
            ? `${spamAttachmentSummary.fileCount} file${spamAttachmentSummary.fileCount === 1 ? '' : 's'} using ${formatBytes(spamAttachmentSummary.totalBytes)}`
            : 'Calculating file storage...'}
        </p>
      </div>
      {confirmingSpamCleanup ? (
        <div className="spam-cleanup-confirm">
          <span>Delete these files from storage?</span>
          <button
            className="btn btn-danger btn-sm"
            onClick={handlePurgeSpamAttachments}
            disabled={purgingSpamAttachments}
          >
            {purgingSpamAttachments ? 'Clearing...' : 'Yes, clear files'}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setConfirmingSpamCleanup(false)}
            disabled={purgingSpamAttachments}
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          className="btn btn-danger btn-sm"
          onClick={() => setConfirmingSpamCleanup(true)}
          disabled={purgingSpamAttachments || (!spamAttachmentSummaryError && (!spamAttachmentSummary || spamAttachmentSummary.fileCount === 0))}
        >
          Clear spam files
        </button>
      )}
    </div>
  );

  return (
    <div className="page">
      {toast && (
        <div className={`board-toast board-toast-${toast.type}`} role="status">
          <span>{toast.message}</span>
          <button type="button" onClick={() => setToast(null)} aria-label="Dismiss notification">&times;</button>
        </div>
      )}
      <div className="container board-page-header">
        <div className="board-header">
          <div>
            <h1 className="page-title">Request Board <span className="page-title-count">{totalCount} request{totalCount !== 1 ? 's' : ''}</span></h1>
          </div>
          <div className="board-header-controls">
            {isAdmin && (
              <Link to="/submit" className="btn btn-secondary btn-sm board-new-btn">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                New
              </Link>
            )}
            <div className="sort-control">
              <select className="form-select form-select-sm sort-select" value={sortBy} onChange={(e) => changeSort(e.target.value)}>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="priority">Priority</option>
                <option value="updated">Last updated</option>
              </select>
            </div>
            <button
              className={`btn btn-secondary btn-sm filter-btn ${showFilters || hasActiveFilters ? 'filter-btn-active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              Filter
              {hasActiveFilters && <span className="filter-btn-dot" />}
            </button>
            {isAdmin && (
              <div className="view-toggle">
                <button className={view === 'list' ? 'active' : ''} onClick={() => { setView('list'); localStorage.setItem('boardView', 'list'); }}>List</button>
                <button className={view === 'board' ? 'active' : ''} onClick={() => { setView('board'); localStorage.setItem('boardView', 'board'); }}>Board</button>
              </div>
            )}
          </div>
        </div>

        {/* Filter bar */}
        {showFilters && (
          <div className="filter-bar glass">
            <div className="filter-bar-search">
              <svg className="filter-bar-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                className="filter-bar-input"
                placeholder="Search requests…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button className="filter-bar-clear" onClick={() => setSearch('')}>&times;</button>
              )}
            </div>

            <div className="filter-bar-section">
              <span className="filter-bar-label">Status</span>
              <div className="filter-bar-chips">
                {groups.map(g => (
                  <button
                    key={g.key}
                    className={`filter-chip ${selectedStatuses.has(g.key) ? 'active' : ''}`}
                    onClick={() => toggleStatus(g.key)}
                  >
                    <span className="filter-chip-dot" style={{ background: STATUS_COLORS[g.key] }} />
                    {g.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-bar-section">
              <span className="filter-bar-label">Location</span>
              <div className="filter-bar-chips">
                {LOCATIONS.map(l => (
                  <button
                    key={l.key}
                    className={`filter-chip ${locationFilter === l.key ? 'active' : ''}`}
                    onClick={() => setLocationFilter(l.key)}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>

            {isAdmin && (
              <div className="filter-bar-section">
                <span className="filter-bar-label">Assign</span>
                <div className="filter-bar-chips">
                  <button className={`filter-chip ${assigneeFilter === 'me' ? 'active' : ''}`} onClick={() => setAssigneeFilter(assigneeFilter === 'me' ? 'all' : 'me')}>
                    My tasks
                  </button>
                  <button className={`filter-chip ${assigneeFilter === 'unassigned' ? 'active' : ''}`} onClick={() => setAssigneeFilter(assigneeFilter === 'unassigned' ? 'all' : 'unassigned')}>
                    Unassigned
                  </button>
                  {trustees.filter(t => t.id !== user?.id).map(t => (
                    <button key={t.id} className={`filter-chip ${assigneeFilter === t.id ? 'active' : ''}`} onClick={() => setAssigneeFilter(assigneeFilter === t.id ? 'all' : t.id)}>
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isAdmin && (
              <div className="filter-bar-section">
                <span className="filter-bar-label">Spam</span>
                <div className="filter-bar-chips">
                  <button
                    className={`filter-chip ${showSpam ? 'filter-chip-spam active' : ''}`}
                    onClick={toggleSpamView}
                  >
                    {showSpam ? 'Showing spam only' : 'Show spam'}
                  </button>
                </div>
              </div>
            )}

            {hasActiveFilters && (
              <button className="filter-bar-reset" onClick={clearFilters}>Clear all filters</button>
            )}
          </div>
        )}
      </div>

      {totalCount === 0 ? (
        <div className="empty-state">
          <p>No requests yet. Be the first to submit one.</p>
          <Link to="/submit" className="btn btn-primary empty-state-btn">Submit a Request</Link>
        </div>
      ) : effectiveView === 'list' ? (
        <div className="grouped-list">
          {spamCleanupToolbar}
          {groupedList.length === 0 ? (
            <div className="empty-state"><p>No requests match your filters.</p></div>
          ) : (
            groupedList.map(group => (
              <div key={group.key} className="ticket-group" style={{ '--group-color': STATUS_COLORS[group.key] }}>
                <button className="ticket-group-header" onClick={() => toggleCollapse(group.key)}>
                  <span className={`ticket-group-chevron ${collapsed[group.key] ? 'collapsed' : ''}`}>›</span>
                  <span className="ticket-group-dot" />
                  <span className="ticket-group-label">{group.label}</span>
                  <span className="ticket-group-count">{group.tickets.length}</span>
                </button>
                {!collapsed[group.key] && (() => {
                  const limit = getVisibleCount(group.key);
                  const visible = group.tickets.slice(0, limit);
                  const remaining = group.tickets.length - limit;
                  return (
                    <div className="ticket-group-items">
                      {visible.map(ticket => {
                        const priority = priorityOf(ticket);
                        return (
                          <div key={ticket.id} className={`ticket-card ${ticket.status === 'spam' ? 'ticket-card-spam' : ''}`} onClick={() => navigate(`/ticket/${ticket.id}`)}>
                            {isAdmin && (
                              <button className={`star-btn ${ticket.starred ? 'starred' : ''}`} onClick={(e) => handleToggleStar(e, ticket.id)} title={ticket.starred ? 'Unstar' : 'Star'}>
                                {ticket.starred ? '★' : '☆'}
                              </button>
                            )}
                            <div className="ticket-card-body">
                              <div className="ticket-card-top">
                                <span className="ticket-card-number">#{ticket.ticket_number}</span>
                                <span className={`badge badge-${priority}`}>{priority}</span>
                                <span className="ticket-card-time">{timeAgo(ticket.created_at)}</span>
                              </div>
                              <h3 className="ticket-card-title">{ticket.title}</h3>
                              <div className="ticket-card-meta">
                                <span className="ticket-card-location">{locationLabel(ticket.location)}</span>
                                {ticket.specific_location && (
                                  <>
                                    <span className="ticket-card-sep">/</span>
                                    <span className="ticket-card-specific">{ticket.specific_location}</span>
                                  </>
                                )}
                                {ticket.update_count > 0 && (
                                  <span className="ticket-card-updates">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                                    {ticket.update_count}
                                  </span>
                                )}
                                {ticket.assigned_trustee_name && (
                                  <span className="ticket-card-assignee">{ticket.assigned_trustee_name}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {(remaining > 0 || limit > DEFAULT_VISIBLE) && (
                        <div className="show-more-row">
                          {remaining > 0 && (
                            <button className="show-more-btn" onClick={() => showMoreList(group.key)}>
                              Show {Math.min(remaining, LOAD_MORE)} more of {group.tickets.length}
                            </button>
                          )}
                          {limit > DEFAULT_VISIBLE && (
                            <button className="show-more-btn" onClick={() => showLessList(group.key)}>
                              Show less
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="container">
          {spamCleanupToolbar}
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="board">
              {visibleColumns.map(col => {
                const isCollapsed = boardCollapsed[col.key];
                return (
                  <Droppable droppableId={col.key} key={col.key}>
                    {(provided, snapshot) => (
                      <div className={`board-column ${snapshot.isDraggingOver ? 'drag-over' : ''}`} ref={provided.innerRef} {...provided.droppableProps}>
                        <button className="board-column-header" style={{ '--col-color': STATUS_COLORS[col.key] }} onClick={() => toggleBoardCollapse(col.key)}>
                          <span className={`board-column-chevron ${isCollapsed ? 'collapsed' : ''}`}>›</span>
                          <div className="board-column-dot" />
                          <span className="board-column-title">{col.label}</span>
                          <span className="board-column-count">{boardGrouped[col.key].length}</span>
                        </button>
                        {!isCollapsed && (() => {
                          const items = boardGrouped[col.key];
                          const limit = getBoardVisibleCount(col.key);
                          const visible = items.slice(0, limit);
                          const remaining = items.length - limit;
                          return (
                            <>
                              {visible.map((ticket, index) => (
                                <Draggable draggableId={ticket.id} index={index} key={ticket.id}>
                                  {(prov, snap) => (
                                    <div className={`board-card glass ${snap.isDragging ? 'dragging' : ''}`} ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps} onClick={() => navigate(`/ticket/${ticket.id}`)}>
                                      <div className="board-card-top">
                                        <button className={`star-btn star-btn-sm ${ticket.starred ? 'starred' : ''}`} onClick={(e) => handleToggleStar(e, ticket.id)}>
                                          {ticket.starred ? '★' : '☆'}
                                        </button>
                                        <span className="board-card-number">#{ticket.ticket_number}</span>
                                        <span className={`badge badge-${priorityOf(ticket)}`}>{priorityOf(ticket)}</span>
                                      </div>
                                      <div className="board-card-title">{ticket.title}</div>
                                      <div className="board-card-meta">
                                        <span className="board-card-location">{locationLabel(ticket.location)}</span>
                                        <span>{timeAgo(ticket.created_at)}</span>
                                        {ticket.assigned_trustee_name && <span className="ticket-meta-gold">{ticket.assigned_trustee_name}</span>}
                                        {ticket.update_count > 0 && (
                                          <span className="ticket-card-updates">
                                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                                            {ticket.update_count}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {(remaining > 0 || limit > DEFAULT_VISIBLE) && (
                                <div className="show-more-row">
                                  {remaining > 0 && (
                                    <button className="show-more-btn" onClick={() => showMoreBoard(col.key)}>
                                      +{remaining} more
                                    </button>
                                  )}
                                  {limit > DEFAULT_VISIBLE && (
                                    <button className="show-more-btn" onClick={() => showLessBoard(col.key)}>
                                      Show less
                                    </button>
                                  )}
                                </div>
                              )}
                            </>
                          );
                        })()}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                );
              })}
            </div>
          </DragDropContext>
        </div>
      )}

      {denialTarget && <DenialModal onConfirm={handleDeny} onCancel={() => setDenialTarget(null)} />}
    </div>
  );
}
